import { Alert, Calendar, Card, Tooltip, Button, Popconfirm, Row, Col, Select, Drawer, Form, Input, DatePicker, Space, Spin, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import ReminderService from '../modules/Reminder/Services/ReminderService';
import { IReminderListResponse } from '../modules/Reminder/Types/Reminder';
import { DeleteOutlined } from '@ant-design/icons';
import { formatTime, openNotification } from '../utils';
import LiveLessonLink from '../components/LiveLesson/LiveLessonLink';
import LiveLessonCreate from '../components/LiveLesson/LiveLessonCreate';
import { NavLink } from 'react-router-dom';
import LiveLessonCreditHistory from '../components/LiveLesson/LiveLessonCreditHistory';
import ReminderAssignmentDetail from '../modules/Reminder/Components/ReminderAssignmentDetail';
import CalendarEventService from '../services/CalendarEventService';
import { CalendarEvent } from '../types/CalendarEvent';

const { Option } = Select;

const ReminderPage = () => {
    const [reminderList, setReminderList] = useState<IReminderListResponse[]>([]);
    const [statusFilter, setStatusFilter] = useState<any[]>([]);
    const [createVisible, setCreateVisible] = useState(false);
    const [form] = Form.useForm();
    const [assignmentDrawerOpen, setAssignmentDrawerOpen] = useState(false);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
    const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
    const [calendarEventsLoading, setCalendarEventsLoading] = useState(false);
    const [calendarEventsError, setCalendarEventsError] = useState<string | null>(null);
    const [calendarYear, setCalendarYear] = useState(dayjs().year());

    const [filter, setFilter] = useState<any>({
        startDate: dayjs().startOf('month').format('YYYY-MM-DDT00:00:00'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DDT23:59:59'),
        status: statusFilter,
    });

    const onPanelChange = (value: Dayjs) => {
        const startOfMonth = value.startOf('month').format('YYYY-MM-DDT00:00:00');
        const endOfMonth = value.endOf('month').format('YYYY-MM-DDT23:59:59');

        setFilter((prev: any) => ({
            ...prev,
            startDate: startOfMonth,
            endDate: endOfMonth,
        }));
        setCalendarYear(value.year());
    };

    const getCalendarEvents = useCallback(async () => {
        setCalendarEventsLoading(true);
        setCalendarEventsError(null);

        try {
            const response = await CalendarEventService.getAll({
                year: calendarYear,
                page: 0,
                size: 100,
                sort: 'eventDate',
                direction: 'asc',
            });
            setCalendarEvents(response.data.content);
        } catch {
            setCalendarEvents([]);
            setCalendarEventsError('Genel Takvim kayıtları yüklenemedi. Lütfen daha sonra tekrar deneyin.');
        } finally {
            setCalendarEventsLoading(false);
        }
    }, [calendarYear]);

    const getReminderList = () => {
        const statusQuery = statusFilter && statusFilter.length > 0
            ? `&status=${statusFilter.join(',')}`
            : '';

        // Include other filters like entityNames if needed in a similar way
        ReminderService.getAll(`?startDate=${filter?.startDate}&endDate=${filter?.endDate}${statusQuery}`)
            .then((response: any) => {
                let data: IReminderListResponse[] = response.data;
                setReminderList(data);
            })
            .catch((e: Error) => {
                console.log(e);
            });
    };

    const handleCreateSubmit = (values: any): void => {
        const data = {
            entityName: values.entityName,
            entityId: '0',
            content: values.content,
            properties: '{}',
            reminderDate: values.reminderDate.format('YYYY-MM-DDTHH:mm:ss'),
        };

        ReminderService.create(data)
            .then(() => {
                getReminderList();
                form.resetFields();
                openNotification('success', 'Başarılı', 'Hatırlatma oluşturuldu.');
                setCreateVisible(false);
            })
            .catch((e: Error) => {
                openNotification('error', 'Hata', 'Kaydetme sırasında bir hata oluştu.');
                console.error('Kaydetme sırasında hata:', e);
            });
    };


    useEffect(() => {
        getReminderList();
    }, [filter, statusFilter]);

    useEffect(() => {
        getCalendarEvents();
    }, [getCalendarEvents]);

    const handleDelete = (reminderId: number) => {
        ReminderService.remove(reminderId)
            .then(() => {
                getReminderList();
                openNotification('success', 'Başarılı', 'Silindi.');
            })
            .catch((e: Error) => {
                openNotification('error', 'Hata', 'Silinme sırasında bir hata oluştu.');
                console.error('Silme sırasında hata:', e);
            });
    };

    const handleStatusChange = (value: string[]) => {
        setStatusFilter(value.length > 0 ? value : []);
        setFilter((prev: any) => ({
            ...prev,
            status: value.length > 0 ? value : [],
        }));
    };


    const dateCellRender = (value: Dayjs) => {
        const dateString = value.format('YYYY-MM-DD');
        const calendarEventsForDay = calendarEvents.filter((calendarEvent) =>
            dayjs(calendarEvent.eventDate).format('YYYY-MM-DD') === dateString
        );
        const remindersForDay = reminderList.filter(reminder =>
            dayjs(reminder.reminderDate).format('YYYY-MM-DD') === dateString
        );

        return (
            <ul className="events" style={{ listStyleType: 'none', paddingLeft: 5 }}>
                {calendarEventsForDay.map((calendarEvent) => (
                    <li key={`calendar-event-${calendarEvent.id}`}>
                        <Tooltip title={calendarEvent.description || calendarEvent.name} placement="top">
                            <Tag className="mr-8" color="purple">Genel Takvim</Tag>
                            <b>{dayjs(calendarEvent.eventDate).format('HH:mm')} - {calendarEvent.name}</b>
                        </Tooltip>
                    </li>
                ))}
                {remindersForDay.map((reminder: any, index) => {
                    let properties: any = {};
                    try {
                        properties = JSON.parse(reminder?.properties || '{}');
                    } catch (err) {
                        properties = {};
                    }
                    return (
                        <li key={index}
                            style={{ cursor: reminder.entityName === 'Assignment' ? 'pointer' : 'default' }}
                            onClick={() => {
                                if (reminder.entityName === 'Assignment') {
                                    setSelectedAssignmentId(Number(reminder.entityId));
                                    setAssignmentDrawerOpen(true);
                                }
                            }}
                        >
                            <Tooltip title={reminder.content} placement="top">
                                {reminder.status === 'PENDING' && reminder.entityName !== 'Assignment' && <Popconfirm
                                    title="Silmek istediğinizden emin misiniz?"
                                    onConfirm={() => handleDelete(reminder?.id)}
                                    okText="Evet"
                                    cancelText="Hayır"
                                >
                                    <DeleteOutlined />
                                </Popconfirm>}
                                {reminder.status === 'ACCEPT' && properties?.link && <LiveLessonLink link={properties.link}></LiveLessonLink>}
                                <b style={{ marginLeft: 8 }} >{formatTime(reminder.reminderDate)}</b> - {(properties?.lessonName || reminder.content)}
                            </Tooltip>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <>
        <Card title="Takvim" bodyStyle={{ paddingTop: 0 }}
            extra={
                <Row gutter={[16, 16]} justify="space-between" align="middle">
                    <Col>
                        <Row justify={"space-between"} align={"middle"}>
                            <LiveLessonCreditHistory />
                        </Row>
                    </Col>
                    <Col>
                        <LiveLessonCreate getLiveLessons={() => { getReminderList(); }} />
                        <Button style={{marginLeft: 15}} size="large" type="primary" onClick={() => setCreateVisible(true)}>Hatırlatıcı Ekle</Button>
                    </Col>
                    <Col>
                        <Select
                            mode="multiple"
                            placeholder="Durum seçin"
                            onChange={handleStatusChange}
                            allowClear
                            size="large"
                            style={{ width: 200 }}
                        >
                            <Option value="PENDING">Beklemede</Option>
                            <Option value="ACCEPT">Onaylandı</Option>
                            <Option value="REJECT">Reddedildi</Option>
                            <Option value="COMPLETED">Tamamlandı</Option>
                        </Select>

                    </Col>
                    <Col>
                        <NavLink to="/">
                            <Button size="large">Geri</Button>
                        </NavLink>
                    </Col>
                </Row>
            }
        >
            {calendarEventsLoading && (
                <Space className="mb-12" size="small">
                    <Spin size="small" />
                    Genel Takvim kayıtları yükleniyor...
                </Space>
            )}
            {calendarEventsError && (
                <Alert
                    className="mb-12"
                    message="Genel Takvim kayıtları gösterilemiyor"
                    description={calendarEventsError}
                    showIcon
                    type="warning"
                />
            )}
            <Calendar
                mode="month"
                onPanelChange={onPanelChange}
                dateCellRender={dateCellRender}
            />
            <Drawer
                title="Hatırlatıcı Ekle"
                placement="right"
                onClose={() => setCreateVisible(false)}
                open={createVisible}
                width={400}
            >
                <Form layout="vertical" form={form} onFinish={handleCreateSubmit}>
                    <Form.Item
                        name="entityName"
                        label="Tür"
                        rules={[{ required: true, message: 'Lütfen tür seçiniz' }]}
                    >
                        <Select placeholder="Seçiniz">
                            <Option value="assignment">Ödev</Option>
                            <Option value="plan">Plan</Option>
                            <Option value="note">Not</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="reminderDate"
                        label="Hatırlatma Tarihi"
                        rules={[{ required: true, message: 'Lütfen hatırlatma tarihi seçiniz' }]}
                    >
                        <DatePicker
                            format="DD/MM/YYYY HH:mm"
                            showTime={{ defaultValue: dayjs('00:00', 'HH:mm'), minuteStep: 5 }}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="content"
                        label="İçerik"
                        rules={[{ required: true, message: 'Lütfen içeriği giriniz' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">Kaydet</Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </Card >
        <ReminderAssignmentDetail
            assignmentId={selectedAssignmentId}
            open={assignmentDrawerOpen}
            onClose={() => setAssignmentDrawerOpen(false)}
        />
        </>
    );
};

export default ReminderPage;
